<template>
    <div class="match-page">
        <match-banner :match="match" :live="isLive" />
        <div class="cl-page">
            <div v-if="match.pdfUrl" class="match-page__actions">
                <button type="button" class="match-page__pdf" @click="downloadStats">
                    <v-icon small>mdi-file-pdf-box</v-icon>Στατιστικά PDF
                </button>
            </div>

            <!-- the video sits on top, and docks to a corner while the stats are read -->
            <match-video v-if="match.videoUrl" :url="match.videoUrl" :live="isLive" />
            <!-- a played match: its result and its MVP as pictures for the socials -->
            <match-share-images v-if="hasScore && !isLive && match.team1 && match.team2" :match="match" :match-id="$route.params.id" :logs="matchLogs" />

            <template v-if="match.team1 && match.team2">
                <!-- not started yet: when, and how the two teams come in -->
                <section v-if="isUpcoming" class="cl-section">
                    <section-heading title="Πριν τον αγώνα" icon="mdi-calendar-clock" />
                    <div class="preview">
                        <div class="preview__when">
                            <v-icon color="#03e5b7">mdi-calendar-outline</v-icon>
                            <span>{{ previewDate }}</span>
                        </div>
                        <div class="preview__teams">
                            <router-link v-for="team in [match.team1, match.team2]" :key="team.id" :to="{ name: 'Team', params: { id: team.id } }" class="preview__team">
                                <span class="cl-logo preview__logo"><img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" alt=""></span>
                                <span class="preview__name">{{ team.name }}</span>
                                <span class="preview__record">
                                    <span><b>{{ team.seasonWins || 0 }}</b> Ν</span>
                                    <span><b>{{ team.seasonLoses || 0 }}</b> Η</span>
                                </span>
                            </router-link>
                        </div>
                    </div>
                </section>

                <template v-else>
                    <section class="cl-section">
                        <section-heading title="Σκορ ανά περίοδο" icon="mdi-timer-outline">
                            <span v-if="isLive" class="match-page__live"><i />LIVE · ανανεώνεται αυτόματα</span>
                        </section-heading>
                        <period-score
                            :team1="match.team1"
                            :team2="match.team2"
                            :score1="match.team1Score"
                            :score2="match.team2Score"
                            :period-scores-team1="getPeriodPoints(match.team1)"
                            :period-scores-team2="getPeriodPoints(match.team2)"
                            :live="isLive"
                            :current-period="currentPeriod"
                        />
                    </section>

                    <!-- the players and the teams on one page: MVP, team against team, leaders, box score -->
                    <main-match-stats-player :match="match" :live="isLive">
                        <template v-slot:compare>
                            <section class="cl-section">
                                <section-heading title="Σύγκριση ομάδων" icon="mdi-scale-balance" />
                                <main-match-stats-info :match="match" />
                            </section>
                        </template>
                    </main-match-stats-player>

                    <section class="cl-section">
                        <section-heading title="Shooting chart" icon="mdi-target" />
                        <div class="shots">
                            <div v-for="side in shotSides" :key="side.team.id" class="shots__card">
                                <div class="shots__head">
                                    <span class="cl-logo shots__logo"><img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt=""></span>
                                    <span class="shots__name">{{ side.team.name }}</span>
                                </div>

                                <!-- the shooting of the team: field goals, 2P, 3P, free throws -->
                                <div class="shots__tiles">
                                    <div v-for="tile in side.tiles" :key="tile.code" class="shots__tile" :class="{ 'shots__tile--lead': tile.code === 'FG' }">
                                        <span class="shots__tile-pct">{{ tile.pct }}<small>%</small></span>
                                        <span class="shots__tile-code">{{ tile.code }}</span>
                                        <span class="shots__tile-ratio">{{ tile.made }}/{{ tile.att }}</span>
                                    </div>
                                </div>

                                <!-- where the points came from -->
                                <div v-if="side.points.total" class="shots__split">
                                    <div class="shots__split-bar">
                                        <span v-for="part in side.points.parts" :key="part.code" :class="`shots__split-${part.code}`" :style="{ width: `${part.share}%` }" />
                                    </div>
                                    <div class="shots__split-legend">
                                        <span v-for="part in side.points.parts" :key="part.code">
                                            <i :class="`shots__split-${part.code}`" />{{ part.label }} <b>{{ part.points }}</b> ({{ part.share }}%)
                                        </span>
                                    </div>
                                </div>

                                <div class="shots__legend">
                                    <span><v-icon x-small color="#03e5b7">mdi-circle</v-icon>{{ side.made }} εύστοχα στο γήπεδο</span>
                                    <span><v-icon x-small color="#ff5a5a">mdi-close-thick</v-icon>{{ side.missed }} άστοχα</span>
                                </div>
                                <div class="shots__court">
                                    <shooting-chart :shots="side.shots" />
                                </div>
                                <router-link v-if="side.shooter" :to="{ path: `/Player/${side.shooter.player.id}` }" class="shots__shooter">
                                    <v-icon small color="#03e5b7">mdi-fire</v-icon>
                                    <span>Πιο εύστοχος: <b>{{ side.shooter.name }}</b> · {{ side.shooter.made }}/{{ side.shooter.att }} εντός πεδιάς</span>
                                </router-link>
                            </div>
                        </div>
                    </section>
                </template>
            </template>
        </div>
    </div>
</template>

<script>
    import MatchBanner from '@/components/MatchBanner'
    import MatchVideo from '@/components/MatchVideo'
    import MatchShareImages from '@/components/MatchShareImages'
    import MainMatchStatsPlayer from '@/components/MainMatchStatsPlayer'
    import MainMatchStatsInfo from '@/components/MainMatchStatsInfo'
    import matchService from '@/services/match'
    import momentTimezone from 'moment-timezone'
    import { getStateFromLogs, LIVE_WINDOW_MINUTES, CHECK_AFTER_START_MINUTES } from '@/utils/matchLive'
    import ShootingChart from '@/components/ShootingChart'
    import PeriodScore from '@/components/PeriodScore'
    import SectionHeading from '@/components/SectionHeading'

    const MINUTE = 60 * 1000
    // while a match can still be live, refresh it this often
    const LIVE_REFRESH_MS = 10 * 1000

    export default {
        components: { MatchBanner, MatchVideo, MatchShareImages, MainMatchStatsPlayer, MainMatchStatsInfo, ShootingChart, PeriodScore, SectionHeading },
        data() {
            return {
                polling: null,
                now: Date.now(),
                matchLogs: [],
                match: {},
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            // the latest period in the play-by-play, for the live marker of the period score
            currentPeriod() {
                const periods = (this.matchLogs || []).map(log => Number(log && log.period)).filter(period => Number.isFinite(period) && period > 0)
                return periods.length ? Math.max(...periods) : null
            },
            // the shooting of each team, next to its chart
            shotSides() {
                return [this.match.team1, this.match.team2].filter(Boolean).map(team => {
                    const sum = { twoPointsMade: 0, twoPointAttempts: 0, threePointsMade: 0, threePointAttempts: 0, freeThrowsMade: 0, freeThrowAttempts: 0 }
                    let shooter = null
                    ;(team.players || []).forEach(player => {
                        const line = player.stats && player.stats[0]
                        if (!line) return
                        Object.keys(sum).forEach(key => { sum[key] += Number(line[key]) || 0 })
                        const made = (Number(line.twoPointsMade) || 0) + (Number(line.threePointsMade) || 0)
                        const att = (Number(line.twoPointAttempts) || 0) + (Number(line.threePointAttempts) || 0)
                        if (made > 0 && (!shooter || made > shooter.made || (made === shooter.made && att < shooter.att))) {
                            shooter = { player, made, att, name: `${player.name || ''} ${player.surname || ''}`.trim() }
                        }
                    })
                    const tile = (code, made, att) => ({ code, made, att, pct: att ? Math.round(made / att * 100) : 0 })
                    const points = [
                        { code: 'two', label: '2P', points: sum.twoPointsMade * 2 },
                        { code: 'three', label: '3P', points: sum.threePointsMade * 3 },
                        { code: 'ft', label: 'Βολές', points: sum.freeThrowsMade }
                    ]
                    const total = points.reduce((all, part) => all + part.points, 0)
                    const shots = this.getShots(team)
                    const made = shots.filter(shot => shot.throwStatus === 'made').length
                    return {
                        team,
                        shots,
                        made,
                        missed: shots.length - made,
                        shooter,
                        tiles: [
                            tile('FG', sum.twoPointsMade + sum.threePointsMade, sum.twoPointAttempts + sum.threePointAttempts),
                            tile('2P', sum.twoPointsMade, sum.twoPointAttempts),
                            tile('3P', sum.threePointsMade, sum.threePointAttempts),
                            tile('FT', sum.freeThrowsMade, sum.freeThrowAttempts)
                        ],
                        points: {
                            total,
                            parts: points.map(part => ({ ...part, share: total ? Math.round(part.points / total * 100) : 0 }))
                        }
                    }
                })
            },
            hasScore() {
                return (Number(this.match.team1Score) || 0) > 0 || (Number(this.match.team2Score) || 0) > 0
            },
            // no score and not live yet: a preview instead of empty stats
            isUpcoming() {
                if (this.isLive || this.hasScore) return false
                return !!this.startTime && this.startTime > this.now
            },
            previewDate() {
                if (!this.match.matchDatetime) return 'Δεν έχει οριστεί ημερομηνία'
                return momentTimezone(this.match.matchDatetime).tz('Europe/Athens').locale('el').format('dddd D MMMM YYYY · HH:mm')
            },
            startTime() {
                return this.match.matchDatetime ? momentTimezone(this.match.matchDatetime).valueOf() : null
            },
            // around tip-off and for a few hours later the match may still change
            inLiveWindow() {
                if (!this.startTime) return false
                return this.now >= this.startTime - 15 * MINUTE && this.now < this.startTime + CHECK_AFTER_START_MINUTES * MINUTE
            },
            // same rule as the big site: the stat logs tell whether "end game" was pressed
            logsState() {
                return getStateFromLogs(this.matchLogs)
            },
            isLive() {
                if (!this.inLiveWindow) return false
                if (this.logsState === 'live') return true
                if (this.logsState === 'ended') return false
                return this.now >= this.startTime && this.now < this.startTime + LIVE_WINDOW_MINUTES * MINUTE
            }
        },
        beforeDestroy() {
            clearInterval(this.polling)
        },
        created() {
            const matchId = this.$route.params.id
            this.fetchMatch(matchId).then(() => this.fetchMatchLogs(matchId))
            // refresh while the match can be live; once "end game" is in the logs there is nothing left to update
            this.polling = setInterval(() => {
                this.now = Date.now()
                if (this.inLiveWindow && this.logsState !== 'ended') {
                    this.fetchMatch(matchId)
                    this.fetchMatchLogs(matchId)
                }
            }, LIVE_REFRESH_MS)
        },
        methods: {
            getPeriodPoints(team) {
                if (!team || !team.players) {
                    return { 1: '-', 2: '-', 3: '-', 4: '-' }
                }
                const periodPoints = {}
                team.players.forEach(player => {
                    if (
                        player.stats &&
                        player.stats.length > 0 &&
                        player.stats[0] &&
                        player.stats[0].periodScore
                    ) {
                        Object.entries(player.stats[0].periodScore).forEach(([period, points]) => {
                            periodPoints[period] = (periodPoints[period] || 0) + points
                        })
                    }
                })
                // Fill missing periods with '-'
                for (let i = 1; i <= 4; i++) {
                    if (periodPoints[i] === undefined) periodPoints[i] = '-'
                }
                return periodPoints
            },
            getShots(team) {
                return (team.players || []).flatMap((player) => {
                    if (!player.stats || player.stats.length === 0 || !player.stats[0].throwPositions) {
                        return []
                    }
                    return player.stats[0].throwPositions.map(shot => ({
                        x: shot.x,
                        y: shot.y,
                        centered: shot.centered,
                        made: shot.made,
                        playerName: `${player.name || ''} ${player.surname || ''}`.trim(),
                        throwStatus: shot.throwStatus
                    }))
                })
            },
            fetchMatchLogs(matchId) {
                return matchService.getMatchLogs(matchId).then(resp => {
                    this.matchLogs = Array.isArray(resp.data) ? resp.data : []
                }).catch(() => {})
            },
            fetchMatch(matchId) {
                return matchService.getMatch(matchId).then(resp => {
                    this.match = resp.data
                })
            },
            downloadStats() {
                window.open(`${this.assetsUrl}/${this.match.pdfUrl}`, '_blank')
            }
        }
    }
</script>

<style scoped>
.match-page__actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 14px;
}

.match-page__pdf {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border-radius: 999px;
    background: #232323;
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.8rem;
    font-weight: 700;
    transition: background 0.15s;
}

.match-page__pdf .v-icon {
    color: #03e5b7 !important;
}

.match-page__pdf:hover {
    background: #009688;
}

.match-page__live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    background: #ff5252;
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.04em;
}

.match-page__live i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #fff;
    animation: match-page-live 1.2s ease-in-out infinite;
}

@keyframes match-page-live {
    0%,
    100% { opacity: 1; }
    50% { opacity: 0.3; }
}

/* ---------- before the match ---------- */
.preview {
    padding: 18px 20px;
    border-radius: 12px;
    background: #232323;
    color: #fff;
    box-shadow: 0 8px 24px rgba(35, 35, 35, 0.12);
    font-family: Roboto, Arial, sans-serif;
}

.preview__when {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 14px;
    margin-bottom: 14px;
    border-bottom: 2px solid rgba(3, 229, 183, 0.35);
    font-weight: 700;
    text-transform: capitalize;
}

.preview__teams {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.preview__team {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    padding: 12px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.06);
    color: #fff !important;
    text-decoration: none;
}

.preview__team:hover .preview__name {
    color: #03e5b7;
}

.preview__logo {
    width: 48px;
    height: 48px;
}

.preview__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.3rem;
    text-transform: uppercase;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.preview__record {
    display: flex;
    gap: 8px;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.6);
}

.preview__record b {
    color: #03e5b7;
    font-size: 1rem;
}

/* ---------- the shots ---------- */
.shots {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
}

.shots__card {
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    border-radius: 12px;
    background: #232323;
    color: #fff;
    box-shadow: 0 8px 24px rgba(35, 35, 35, 0.12);
    font-family: Roboto, Arial, sans-serif;
}

.shots__head {
    display: flex;
    align-items: center;
    gap: 10px;
    align-self: stretch;
    min-width: 0;
}

.shots__logo {
    width: 36px;
    height: 36px;
}

.shots__name {
    overflow: hidden;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.3rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.shots__tiles {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    align-self: stretch;
    margin-top: 14px;
}

.shots__tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 4px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
}

.shots__tile--lead {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    border-color: transparent;
}

.shots__tile-pct {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.7rem;
    line-height: 1;
}

.shots__tile-pct small {
    font-size: 0.9rem;
}

.shots__tile-code {
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: #03e5b7;
}

.shots__tile--lead .shots__tile-code {
    color: #fff;
}

.shots__tile-ratio {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.55);
}

.shots__tile--lead .shots__tile-ratio {
    color: rgba(255, 255, 255, 0.85);
}

.shots__split {
    align-self: stretch;
    margin-top: 12px;
}

.shots__split-bar {
    display: flex;
    height: 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    overflow: hidden;
}

.shots__split-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin-top: 6px;
    font-size: 0.74rem;
    color: rgba(255, 255, 255, 0.6);
}

.shots__split-legend span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.shots__split-legend b {
    color: #fff;
}

.shots__split-legend i {
    width: 8px;
    height: 8px;
    border-radius: 2px;
}

.shots__split-two {
    background: #03e5b7;
}

.shots__split-three {
    background: #037ade;
}

.shots__split-ft {
    background: #ffc107;
}

.shots__shooter {
    display: flex;
    align-items: center;
    gap: 6px;
    align-self: stretch;
    margin-top: 12px;
    padding: 8px 10px;
    border-radius: 8px;
    background: rgba(3, 229, 183, 0.08);
    color: rgba(255, 255, 255, 0.75) !important;
    font-size: 0.8rem;
    text-decoration: none;
}

.shots__shooter b {
    color: #fff;
}

.shots__shooter:hover b {
    color: #03e5b7;
}

.shots__legend {
    display: flex;
    gap: 14px;
    align-self: stretch;
    margin: 12px 0 10px;
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.65);
}

.shots__legend span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.shots__court {
    width: 100%;
    max-width: 380px;
    border-radius: 8px;
    overflow: hidden;
}

.shots__court >>> .container {
    width: 100%;
    margin: 0;
}

@media (max-width: 700px) {
    .shots,
    .preview__teams {
        grid-template-columns: minmax(0, 1fr);
    }

    .shots__card {
        padding: 12px;
    }

    .shots__tiles {
        gap: 6px;
    }

    .shots__tile-pct {
        font-size: 1.35rem;
    }

    .preview {
        padding: 14px 12px;
    }
}
</style>
